import { type VoidFunctionComponent } from 'react';
import { useState } from 'react';
import { Modal } from 'react-bootstrap';
import type Stripe from 'stripe';

import { FREE_PLAN_NAME } from '../../../../../shared/subscriptions';
import { useAddToast } from '../../../../contexts/ToastContext';
import { window } from '../../../../globals';
import { getBaseOrgProperties, track } from '../../../../helpers/analytics';
import { LONG_DATE_FORMAT } from '../../../../helpers/date';
import { useOnMount } from '../../../../hooks';
import { useSynchronizeSubscription } from '../../../../hooks/api/subscriptions/useSynchronizeSubscription';
import { useUpdatePlan } from '../../../../hooks/api/subscriptions/useUpdatePlan';
import { BUTTON_SIZE, BUTTON_TYPE, LoaderButton } from '../../../generic/Button';
import Card from '../../../generic/Card/Card';
import Loader from '../../../generic/Loader';
import FeaturesComponent from '../Features';
import LimitsComponent from '../Limits';
import { getDynamicPlanName } from '../utils';

import ChurnFormModal from './ChurnFormModal';
import ModalFeaturesLost from './ModalFeaturesLost';
import { usePlansContext } from './PlansContext';

import styles from './styles.module.less';

type MessageModalProps = {
  onHide: () => void;
};

const MessageModal: VoidFunctionComponent<MessageModalProps> = ({
  onHide,
}: MessageModalProps) => {
  const {
    isLoading,
    mutate: synchronizeSubscription,
  } = useSynchronizeSubscription();

  const {
    currentPlan,
    isLoading: isLoadingCurrentPlan,
  } = usePlansContext();

  useOnMount(synchronizeSubscription);

  let text;
  const currentPlanEndDate = currentPlan?.endDate;
  if (currentPlan?.nextScheduledPrice?.id && currentPlanEndDate) {
    const date = new Date(currentPlanEndDate * 1000);
    text = `Your account will be downgraded to our ${getDynamicPlanName(currentPlan?.nextScheduledPrice?.product as Stripe.Product)} Plan at the end of your current billing period on ${LONG_DATE_FORMAT.format(date)}.`;
  } else {
    text = `You are now subscribed to our ${getDynamicPlanName(currentPlan?.price?.product as Stripe.Product)} Plan.`;
  }

  return (
    <Modal show onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title className={styles.modalUpdatePlanTitle}>
          {
            (isLoading || isLoadingCurrentPlan)
              ? 'Updating...'
              : 'You downgraded your plan'
          }
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className={styles.modalUpdatePlanBody}>
        {
          (isLoading || isLoadingCurrentPlan)
            ? <Loader />
            : <p>{text}</p>
        }
      </Modal.Body>
    </Modal>
  );
};

type GetButtonTextParams = {
  endDate?: number;
  isCurrentPlan: boolean;
  isNextPlan: boolean;
};

const getButtonText = ({ endDate, isCurrentPlan, isNextPlan }: GetButtonTextParams) => {
  if (isCurrentPlan) {
    return 'Current Plan';
  }

  if (isNextPlan && endDate) {
    return `Your subscription starts on ${LONG_DATE_FORMAT.format(new Date(endDate * 1000))}`;
  }

  return 'Downgrade Plan';
};

/**
 * Renders the price component specifically for the FREE plan.
 * Displays information related to the free pricing tier.
 */
const DisplayFreePlan: VoidFunctionComponent = () => {
  const { currentPlan, freePrice: price } = usePlansContext();

  const [
    shouldShowModal,
    setShouldShowModal,
  ] = useState<boolean>(false);

  const [
    shouldShowBenefitsModal,
    setShouldShowBenefitsModal,
  ] = useState<boolean>(false);

  const [
    shouldShowChurnModal,
    setShouldShowChurnModal,
  ] = useState<boolean>(false);

  const {
    isLoading: isUpdating,
    mutateAsync: updatePlan,
  } = useUpdatePlan();

  const addToast = useAddToast();

  if (!currentPlan) {
    return null;
  }

  const isNextPlan = currentPlan.nextScheduledPrice?.id === price.id;
  const isSelectedCurrent = currentPlan.plan === price.id || currentPlan.plan === FREE_PLAN_NAME;

  const onChurnFormSubmit = () => {
    setShouldShowChurnModal(false);
    track(`Downgrade Confirmed - Churn Modal`, getBaseOrgProperties());
    updatePlan(price.id)
      .then(() => setShouldShowModal(true))
      .catch((e) => {
        addToast({ type: 'error', content: `An unexpected error happened. Error message: ${e.message}` });
      });
  };

  return (
    <>
      {
        shouldShowModal &&
        <MessageModal
          onHide={() => {
            window.location.reload();
            setShouldShowModal(false);
          }}
        />
      }
      <ModalFeaturesLost
        isShowing={shouldShowBenefitsModal}
        onConfirm={() => {
          setShouldShowBenefitsModal(false);
          setShouldShowChurnModal(true);
        }}
        onHide={() => setShouldShowBenefitsModal(false)}
      />
      <ChurnFormModal
        isShown={shouldShowChurnModal}
        onHide={() => {
          track(`Downgrade Cancelled - Churn Modal`, getBaseOrgProperties());
          setShouldShowChurnModal(false);
        }}
        onFormSubmit={onChurnFormSubmit}
      />
      <span className={styles.price}>$0/month</span>
      <Card
        isFullWidth
        padding="xs"
        variant="outline"
      >
        <Card.Content>
          <span className={styles.priceTierDescription}>One size fits all</span>
        </Card.Content>
      </Card>
      <LoaderButton
        size={BUTTON_SIZE.LARGE}
        type={isSelectedCurrent || isNextPlan ? BUTTON_TYPE.SECONDARY : BUTTON_TYPE.ERROR}
        isFullWidth
        isLoading={isUpdating}
        onClick={() => setShouldShowBenefitsModal(true)}
        isDisabled={isSelectedCurrent || isNextPlan}
      >
        { getButtonText({ endDate: currentPlan.endDate, isCurrentPlan: isSelectedCurrent, isNextPlan }) }
      </LoaderButton>

      <LimitsComponent product={price.product as Stripe.Product} />
      <FeaturesComponent product={price.product as Stripe.Product} />
    </>
  );
};

export default DisplayFreePlan;
